/**
 * EventTicker — a live activity feed over the canvas, newest first. Reads the
 * MFP feedback store (progress / completion / error / input messages).
 */

import { motion, AnimatePresence } from "framer-motion";
import { Waypoints } from "lucide-react";
import { useFeedbackStore } from "../../../stores/feedback.js";
import { feedbackPreview } from "./events.js";

const TYPE_TONE: Record<string, string> = {
  progress: "text-brand-cyan",
  completion: "text-brand-green",
  error: "text-brand-pink",
  "request-for-input": "text-status-paused",
};

export function EventTicker() {
  const messages = useFeedbackStore((s) => s.messages);
  const recent = messages.slice(-12).reverse();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ type: "spring", duration: 0.4, bounce: 0 }}
      className="overlay-panel pointer-events-auto flex w-80 flex-col rounded-2xl"
    >
      <header className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
        <Waypoints className="h-4 w-4 text-brand-cyan" />
        <h2 className="text-sm font-medium text-text-primary">Activity</h2>
      </header>

      <div className="max-h-64 space-y-0.5 overflow-y-auto p-2">
        {recent.length === 0 ? (
          <p className="px-2 py-3 text-center text-xs text-text-muted">Waiting for activity…</p>
        ) : (
          <AnimatePresence initial={false}>
            {recent.map((m) => {
              const preview = feedbackPreview(m.payload);
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
                  className="flex items-baseline gap-2 rounded-lg px-2 py-1.5"
                >
                  <span
                    className={`shrink-0 font-mono text-[10px] uppercase tracking-wide ${
                      TYPE_TONE[m.type] ?? "text-text-muted"
                    }`}
                  >
                    {m.type}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
                    <span className="text-text-muted">{m.source?.name ?? "system"}</span>
                    {preview && <span className="text-text-secondary"> · {preview}</span>}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
}
